import * as yup from "yup";

import {
  MULTIMEDIA_MIME_TYPES,
  MULTIMEDIA_PLACEHOLDER,
  FILE_ALREADY_EXISTS,
} from "../../../../../utils/constants";

import { useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import { useState, useEffect } from "react";
import { FaCircleNotch } from "react-icons/fa";
import { IoSaveOutline } from "react-icons/io5";

import Textbox from "./../../../../../components/common/Textbox";
import ReactSelect from "./../../../../../components/common/ReactSelect";
import ReactSelectAsync from "./../../../../../components/common/ReactSelectAsync";
import Datepicker from "./../../../../../components/common/Datepicker";
import FileUpload from "./../../../../../components/common/FileUpload";
import Textarea from "../../../../../components/common/Textarea";
import Toaster from "./../../../../../components/common/Toaster";

import taxService from "./../../../../../services/taxService";
import complianceService from "../../../../../services/complianceService";
import moment from "moment";

const TaxForm = ({ id, data, account, isLoading, setLoading, onSuccess }) => {
  const quarters = taxService.getTaxQuarters();
  const periodTypes = taxService.getPeriodTypes();
  const taxTypes = taxService.getTaxTypes();

  const [fileExists, setFileExists] = useState(false);
  const [isDisabled, setIsDisabled] = useState(false);

  const [periodOptions, setPeriodOptions] = useState(quarters);

  const [cor, setCor] = useState(null);
  const [filingDate, setFilingDate] = useState(null);
  const [paymentDate, setPaymentDate] = useState(null);
  const [year, setYear] = useState(null);

  const [currentYear] = useState(
    Date.parse(moment(new Date(), "YYYY").toISOString())
  );

  const schema = yup.object({
    remarks: yup.string().nullable(),
    email_send: yup.bool(),
    amount: yup.string().required("Please enter amount."),
    year: yup.string().required("Please select year."),
    filing_date: yup.string().required("Please select filing date."),
    payment_date: yup.string().required("Please select payment date."),
    cor: yup
      .object()
      .shape({
        label: yup.string(),
        value: yup.string(),
      })
      .nullable(),
    tax_type: yup.object().shape({
      label: yup.string().required("Please select tax type."),
      value: yup.string().required("Please select tax type."),
    }),
    return_period: yup
      .object()
      .shape({
        label: yup.string().required("Please select return period."),
        value: yup.string().required("Please select return period."),
      })
      .nullable(),
    return_period_type: yup.object().shape({
      label: yup.string().required("Please select return period type."),
      value: yup.string().required("Please select return period type."),
    }),
    files: yup
      .mixed()
      .test(
        "is-valid-file-name",
        "A string in a file name must match with the first name.",
        (value) => isValidFileName(value)
      ),
  });

  const {
    register,
    handleSubmit,
    setValue,
    clearErrors,
    control,
    formState: { errors },
  } = useForm({ resolver: yupResolver(schema) });

  const options = async (search, loadedOptions, { page }) => {
    const params = {
      status: "",
      start_date: "",
      end_date: "",
      search_query: search,
      current_page: page,
      page_size: 10,
      sort_column: { path: "created_on", order: "desc" },
    };

    const response = await complianceService.getAccountCORsAsync(id, params);
    const { result: data, count, total_count } = response;

    const options = data.map((item) => {
      item["label"] = item.ocn;
      item["value"] = item.id;

      return item;
    });

    const hasMore = total_count > count;

    return {
      options,
      hasMore,
      additional: {
        page: page + 1,
      },
    };
  };

  const onSubmit = async (info) => {
    try {
      setLoading(true);

      const tax = { ...info };
      if (data) tax["id"] = data.id;

      await taxService.saveAccountTaxAsync(id, tax);
      Toaster.Success("Tax Saved Successfully");
      onSuccess();
    } catch (ex) {
      if (ex.response && ex.response.data.message === FILE_ALREADY_EXISTS) {
        setFileExists(true);
      } else {
        Toaster.Error("Something went wrong.");
      }
    } finally {
      setLoading(false);
    }
  };

  const handleCorChange = (item) => {
    const option = {
      value: item.id,
      label: item.ocn,
    };

    setValue("cor", option);
    clearErrors("cor");
    setCor(item);
  };

  const handleIncomeTypeChange = (e) => {
    if (e.value === 6) {
      const quarter = { value: 2, label: "Quarterly" };
      setValue("return_period_type", quarter);
      clearErrors("return_period_type");
      setIsDisabled(false);
      return;
    }

    if (e.value === 4) {
      const annual = { value: 3, label: "Annual" };
      setValue("return_period_type", annual);
      clearErrors("return_period_type");
      setIsDisabled(true);
      return;
    }

    const quarter = { value: 2, label: "Quarterly" };
    setValue("return_period_type", quarter);
    clearErrors("return_period_type");
    setPeriodOptions(quarters);
    setIsDisabled(true);
  };

  const handlePeriodTypeChange = (e) => {
    const options = taxService.getReturnPeriods(e.value);
    setPeriodOptions(options);
  };

  const isValidFileName = (file) => {
    if (file === undefined) return true;

    let first_name = account.first_name;
    let file_name = file[0].name;

    first_name = first_name.toLowerCase();
    file_name = file_name.toLowerCase();

    return file_name.includes(first_name);
  };

  useEffect(() => {
    if (data) {
      console.log(data);

      const selected_quarter = taxService.getSelectedTaxQuarter(
        data.quarter_value
      );

      const selected_cor = {
        value: data.certificate_of_registration_id,
        label: data.ocn,
      };

      const selected_tax_type = {
        value: data.tax_type,
        label: data.tax_type_label,
      };

      let period_type_name =
        data.return_period_type === 1 ? "Monthly" : "Quarterly";

      if (data.tax_type === 4) {
        period_type_name = "Annual";
        setIsDisabled(true);
      }

      let selected_period_type = {
        value: data.return_period_type,
        label: period_type_name,
      };

      if (data.return_period_type === 0)
        selected_period_type = {
          value: 2,
          label: "Quarterly",
        };

      let selected_return_period = {
        value: data.return_period_value,
        label: data.return_period_name,
      };

      if (data.return_period_value === 0) selected_return_period = {};

      const options = taxService.getReturnPeriods(data.return_period_type);
      setPeriodOptions(options);

      if (data.ocn !== null) setValue("cor", selected_cor);
      setValue("transaction_id", data.transaction_id);
      setValue("amount", data.amount_value);
      setValue("quarter", selected_quarter);
      setValue("tax_type", selected_tax_type);
      setValue("return_period_type", selected_period_type);
      setValue("return_period", selected_return_period);
      setValue(
        "filing_date",
        moment(data.filing_date_value).format("MM-DD-yyyy")
      );
      setValue(
        "payment_date",
        moment(data.payment_date_value).format("MM-DD-yyyy")
      );

      if (data.year) {
        setValue("year", moment(data.year).format("yyyy"));
      } else {
        setValue("year", moment().format("yyyy"));
      }

      setFilingDate(moment(data.filing_date_value));
      setPaymentDate(moment(data.payment_date_value));
      setYear(moment(data.year));
    }
  }, [data, setValue]);

  return (
    <form onSubmit={handleSubmit(onSubmit)} className="p-8 space-y-6">
      <div className="grid grid-cols-2 gap-6">
        <div className="col-span-2">
          <ReactSelectAsync
            id="cor-dropdown"
            name="cor"
            title="COR"
            options={options}
            control={control}
            onSelectChange={handleCorChange}
            register={register("cor")}
            errors={errors}
            optional
          />
        </div>
        {cor && (
          <div className="col-span-2">
            <div className="p-3 bg-blue-100 rounded-lg border border-blue-300">
              <div className="flex justify-between items-start">
                <div>
                  <p className="text-sm text-gray-600">{cor.ocn}</p>
                  <div className="text-xs text-gray-500">{cor.rdo}</div>
                </div>
                <div>
                  <span className="text-gray-500 text-xs">
                    {cor.is_main_branch ? "Main" : "Branch"}
                  </span>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
      <div className="grid grid-cols-2 space-x-4">
        <div className="col-span-2">
          <ReactSelect
            id="tax-type-dropdown"
            name="tax_type"
            title="Tax Type"
            options={taxTypes}
            control={control}
            onSelectChange={handleIncomeTypeChange}
            register={register("tax_type")}
            errors={errors}
          />
        </div>
      </div>
      <div className="grid grid-cols-2 space-x-4">
        <div className="col-span-1">
          <Datepicker
            id="filing-date-datepicker"
            name="filing_date"
            title="Date of Filing"
            placeholder="MM-DD-YYYY"
            dateFormat="MM-DD-yyyy"
            setValue={setValue}
            clearErrors={clearErrors}
            register={register("filing_date")}
            timeFormat={false}
            onDateChange={(date) => setFilingDate(date)}
            errors={errors}
            value={filingDate}
            readOnly
          />
        </div>
        <div className="col-span-1">
          <Datepicker
            id="payment-date-datepicker"
            name="payment_date"
            title="Payment Date"
            placeholder="MM-DD-YYYY"
            dateFormat="MM-DD-yyyy"
            setValue={setValue}
            clearErrors={clearErrors}
            register={register("payment_date")}
            timeFormat={false}
            onDateChange={(date) => setPaymentDate(date)}
            errors={errors}
            value={paymentDate}
            readOnly
          />
        </div>
      </div>
      <div className="grid grid-cols-3 space-x-4">
        <div className="col-span-2">
          <Textbox
            id="amount-textbox"
            name="amount"
            type="text"
            title="Amount"
            placeholder="0.00"
            register={register("amount")}
            errors={errors}
          />
        </div>
        <div className="col-span-1">
          <Datepicker
            id="year-datepicker"
            name="year"
            title="Year"
            dateFormat="YYYY"
            setValue={setValue}
            clearErrors={clearErrors}
            register={register("year")}
            timeFormat={false}
            initialValue={currentYear}
            onDateChange={(date) => setYear(date)}
            value={year}
            errors={errors}
            readOnly
          />
        </div>
      </div>
      <div className="grid grid-cols-2 space-x-4">
        <div className="col-span-1">
          <ReactSelect
            id="return-period-type-dropdown"
            name="return_period_type"
            title="Return Period Type"
            options={periodTypes}
            control={control}
            isDisabled={isDisabled}
            onSelectChange={handlePeriodTypeChange}
            register={register("return_period_type")}
            errors={errors}
          />
        </div>
        <div className="col-span-1">
          <ReactSelect
            id="return-period-dropdown"
            name="return_period"
            title="Tax Return Period"
            options={periodOptions}
            control={control}
            register={register("return_period")}
            errors={errors}
          />
        </div>
      </div>
      {!data && (
        <div className="grid grid-cols-2">
          <div className="col-span-2">
            <FileUpload
              id="files-dropdown"
              name="files"
              title="Files"
              setValue={setValue}
              clearErrors={clearErrors}
              register={register("files")}
              errors={errors}
              accepts={MULTIMEDIA_MIME_TYPES}
              placeholder={MULTIMEDIA_PLACEHOLDER}
              maxFiles={1}
              maxSize={5242880}
              multiple={false}
            />
            {fileExists && (
              <div className="pt-2">
                <span className="text-sm text-red-400">
                  {FILE_ALREADY_EXISTS}
                </span>
              </div>
            )}
          </div>
        </div>
      )}
      {!data && (
        <div className="grid grid-cols-2">
          <div className="col-span-2">
            <input
              id="email-send-checkbox"
              name="email_send"
              type="checkbox"
              defaultChecked={true}
              {...register("email_send")}
              className="text-blue-500 cursor-pointer border-gray-300 rounded dark:border-gray-600"
            />
            <label className="ml-3 text-sm text-gray-400">
              Send Files via Email
            </label>
          </div>
        </div>
      )}
      <div className="grid grid-cols-2">
        <div className="col-span-2">
          <Textarea
            id="remarks-textarea"
            name="remarks"
            title="Remarks"
            placeholder="Say something..."
            register={register("remarks")}
            errors={errors}
            rows={2}
            optional
          />
        </div>
      </div>
      <div className="grid grid-2 pt-2">
        <div className="col-span-2">
          <button
            type="submit"
            className="button button-primary disabled:opacity-70 disabled:cursor-not-allowed"
            disabled={isLoading}
          >
            {isLoading && <FaCircleNotch className="animate-spin mr-3" />}
            {!isLoading && <IoSaveOutline className="mr-3" />}
            Save Changes
          </button>
        </div>
      </div>
    </form>
  );
};

export default TaxForm;
